<template>
    <DefaultHeader />
    <main v-if="!$store.state.hiddenBody" class="badges-page">
        <div class="page-container">
            <header class="page-heading">
                <div>
                    <div class="eyebrow">GESTION DES RESSOURCES</div>
                    <h1>Badges</h1>
                    <p>Consultez votre parc de badges et gérez leurs affectations.</p>
                </div>
            </header>

            <div v-if="pageError" class="message error" role="alert">{{ pageError }} <button type="button"
                    @click="loadData">Réessayer</button></div>
            <div v-if="feedback" class="message success" role="status">{{ feedback }}</div>
            <div ref="workspace" class="workspace"
                :class="{ 'can-manage': canManage, 'has-editor': canManage && selectedBadgeId != null }">
                <section ref="listPanel" class="list-panel" aria-labelledby="badge-list-title"
                    :tabindex="selectedBadgeId != null ? 0 : undefined"
                    :style="listContentWidth ? { '--list-content-width': `${listContentWidth}px` } : undefined">
                    <div class="panel-toolbar">
                        <div>
                            <h2 id="badge-list-title">Répertoire</h2>
                            <p>{{ filteredBadges.length }} badge{{ filteredBadges.length > 1 ? 's' : '' }} affiché{{
                                filteredBadges.length > 1 ? 's' : '' }}</p>
                        </div>
                        <div class="filters">
                            <label class="search-wrap"><span class="sr-only">Rechercher un badge</span><svg
                                    viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m16.5 16.5 4 4" />
                                </svg><input v-model.trim="search" type="search"
                                    placeholder="Référence, numéro, chauffeur, véhicule…" /></label>
                            <div class="status-tabs" role="group" aria-label="Filtrer les badges par état">
                                <button type="button" :class="{ active: statusFilter === 'NEW' }"
                                    :aria-pressed="statusFilter === 'NEW'" @click="statusFilter = 'NEW'">À valider ({{
                                        newCount }})</button>
                                <button type="button" :class="{ active: statusFilter === 'ACTIVE' }"
                                    :aria-pressed="statusFilter === 'ACTIVE'" @click="statusFilter = 'ACTIVE'">Actifs
                                    ({{ activeCount }})</button>
                                <button type="button" :class="{ active: statusFilter === 'DISABLED' }"
                                    :aria-pressed="statusFilter === 'DISABLED'"
                                    @click="statusFilter = 'DISABLED'">Désactivés ({{ disabledCount }})</button>
                                <button type="button" :class="{ active: statusFilter === 'ALL' }"
                                    :aria-pressed="statusFilter === 'ALL'" @click="statusFilter = 'ALL'">Tous ({{
                                        badges.length }})</button>
                            </div>
                        </div>
                    </div>
                    <div v-if="loading" class="state" role="status"><span class="loader"></span> Chargement des badges…
                    </div>
                    <div v-else-if="!badges.length" class="state">
                        <h3>Aucun badge</h3>
                        <p>Aucun badge n'est enregistré pour ce client.</p>
                    </div>
                    <div v-else-if="!filteredBadges.length" class="state">
                        <h3>Aucun résultat</h3>
                        <p>Modifiez votre recherche ou changez de statut.</p><button type="button" class="text-button"
                            @click="search = ''">Effacer la recherche</button>
                    </div>
                    <BadgeList v-else :badges="filteredBadges" :can-manage="canManage" :selected-id="selectedBadgeId"
                        @edit="selectBadge" />
                </section>
                <aside v-if="canManage && selectedBadgeId != null" ref="editorPanel" class="editor-panel"
                    :style="{ '--editor-offset': editorOffset + 'px' }" aria-label="Modification du badge">
                    <BadgeEditor :key="selectedBadgeId" :badge="selectedBadge" :drivers="drivers" :vehicles="vehicles"
                        :vehicles-loading="vehiclesLoading" :vehicles-error="vehiclesError" :saving="saving"
                        :error="formError" @save="saveBadge" @assign="assignBadge" @unassign="unassignBadge"
                        @assign-vehicle="assignVehicle" @unassign-vehicle="unassignVehicle"
                        @retry-vehicles="loadVehicles" @cancel="closeEditor" />
                </aside>
            </div>
        </div>
    </main>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import BadgeRepository from '@/repositories/badge.repo.js';
import BaliseRepository from '@/repositories/balise.repo.js';
import DriverRepository from '@/repositories/driver.repo.js';
import BadgeList from './sections/BadgeList.vue';
import BadgeEditor from './sections/BadgeEditor.vue';

export default {
    name: 'BadgesManagement',
    components: { DefaultHeader, DefaultFooter, BadgeList, BadgeEditor },
    data() { return { badges: [], drivers: [], vehicles: [], vehiclesLoading: false, vehiclesError: '', loading: false, saving: false, search: '', statusFilter: 'ACTIVE', selectedBadgeId: null, editorOffset: 0, listContentWidth: 0, editorResizeObserver: null, pageError: '', formError: '', feedback: '' }; },
    computed: {
        canManage() { return Boolean(this.$isClientAdmin || this.$isAdmin); },
        selectedBadge() { return this.badges.find(b => String(b.id) === String(this.selectedBadgeId)) || null; },
        newCount() { return this.badges.filter(b => b.status === 'NEW').length; },
        activeCount() { return this.badges.filter(b => b.status === 'ACTIVE').length; },
        disabledCount() { return this.badges.filter(b => b.status === 'DISABLED').length; },
        filteredBadges() {
            const normalize = value => String(value ?? '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLocaleLowerCase('fr');
            const query = normalize(this.search.trim());

            return this.badges.filter(b => {
                if (this.statusFilter !== 'ALL' && b.status !== this.statusFilter) return false;
                if (!query) return true;

                const driver = this.driverFor(b);
                const vehicles = b.balises ?? [];
                const statusLabel = {
                    NEW: 'À valider',
                    ACTIVE: 'Actif',
                    DISABLED: 'Désactivé'
                }[b.status] ?? b.status;

                const fields = [
                    b.ref || 'Sans référence',
                    b.slot != null ? `Slot balise : ${b.slot}` : 'Slot balise : —',
                    b.number || '—',
                    b.driverId ? `Chauffeur #${b.driverId}` : 'Non attribué',
                    driver?.firstName,
                    driver?.lastName,
                    `${driver?.firstName || ''} ${driver?.lastName || ''}`,
                    `${driver?.lastName || ''} ${driver?.firstName || ''}`,
                    vehicles.length ? null : 'Aucun véhicule',
                    ...vehicles.flatMap(vehicle => [vehicle.name, vehicle.baliseId]),
                    statusLabel,
                    this.formatLastActivity(b.lastActivity)
                ];

                return fields.some(value => normalize(value).includes(query));
            });
        }
    },
    watch: {
        // Si la liste se réordonne ou se filtre, conserver l'alignement vertical.
        filteredBadges() {
            this.$nextTick(() => this.positionEditor());
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        // Recalculer l'alignement si le contenu du formulaire change de hauteur
        // (véhicules associés, messages d'erreur, etc.).
        if (typeof ResizeObserver !== 'undefined') {
            this.editorResizeObserver = new ResizeObserver(() => this.positionEditor());
        }
        this.loadData();
        if (this.canManage) this.loadVehicles();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
        this.editorResizeObserver?.disconnect();
    },
    methods: {
        driverFor(badge) { return this.drivers.find(d => String(d.id) === String(badge.driverId)) || badge.driver || null; },
        formatLastActivity(value) {
            if (!value) return 'Jamais';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        },
        async loadVehicles() {
            this.vehiclesLoading = true;
            this.vehiclesError = '';
            try {
                this.vehicles = await BaliseRepository.getClientBalises();
            } catch (err) {
                this.vehicles = [];
                this.vehiclesError = 'Impossible de charger les véhicules du client.';
                console.error('Chargement des véhicules :', err);
            } finally {
                this.vehiclesLoading = false;
            }
        },
        async loadData({ silent = false } = {}) {
            // Les enregistrements ne doivent pas retirer la table du DOM :
            // cela provoquerait un saut de page en éditant un badge situé en bas.
            if (!silent) this.loading = true;
            this.pageError = '';
            try {
                const [badges, drivers] = await Promise.all([BadgeRepository.getBadges(), DriverRepository.getDrivers()]);
                this.drivers = drivers;
                this.badges = badges.map(b => ({
                    ...b,
                    driver: drivers.find(d => String(d.id) === String(b.driverId)) || b.driver || null
                }));
            } catch (err) { this.pageError = 'Impossible de charger les badges ou les chauffeurs.'; console.error(err); }
            finally { if (!silent) this.loading = false; }
        },
        onResize() {
            // La largeur de référence reste celle de l'espace complet, même en édition.
            if (this.selectedBadgeId != null) {
                this.listContentWidth = Math.max(0, (this.$refs.workspace?.clientWidth || 0) - 2);
            }
            this.$nextTick(() => this.positionEditor());
        },
        selectedRow() {
            const rows = this.$refs.listPanel?.querySelectorAll('[data-badge-id]') || [];
            return Array.from(rows).find(row => row.dataset.badgeId === String(this.selectedBadgeId)) || null;
        },
        selectBadge(badge) {
            if (this.saving || String(this.selectedBadgeId) === String(badge.id)) return;

            // Capturer la largeur avant de réduire la zone visible de la liste.
            // Sur une autre sélection, conserver la même largeur de contenu.
            if (this.selectedBadgeId == null) {
                this.listContentWidth = this.$refs.listPanel?.clientWidth || 0;
            }
            this.selectedBadgeId = badge.id;
            this.editorOffset = 0;
            this.formError = '';
            this.feedback = '';

            this.$nextTick(() => {
                this.editorResizeObserver?.disconnect();
                if (this.$refs.editorPanel) {
                    this.editorResizeObserver?.observe(this.$refs.editorPanel);
                }
                // Aucun scroll automatique : l'utilisateur garde sa position.
                this.positionEditor();
            });
        },
        positionEditor() {
            const workspace = this.$refs.workspace;
            const editor = this.$refs.editorPanel;
            if (!workspace || !editor) return;

            // Une seule colonne sur écran étroit : disposition normale, sans scroll forcé.
            if (window.matchMedia('(max-width: 1100px)').matches) {
                if (this.editorOffset !== 0) this.editorOffset = 0;
                return;
            }

            const row = this.selectedRow();
            if (!row) return;

            const rowRect = row.getBoundingClientRect();
            const workspaceRect = workspace.getBoundingClientRect();
            const rowCenter = rowRect.top + rowRect.height / 2;

            // Centrer verticalement la partie utile du formulaire en face du badge.
            // Si le formulaire est plus grand que l'écran, on centre sa partie
            // visible, pas toute sa hauteur (sinon son en-tête disparaîtrait).
            const visibleEditorHeight = Math.min(editor.offsetHeight, Math.max(0, window.innerHeight - 48));
            const desiredTop = Math.max(16, rowCenter - visibleEditorHeight / 2);
            const offset = Math.max(0, Math.round(desiredTop - workspaceRect.top));

            if (this.editorOffset !== offset) this.editorOffset = offset;
        },
        closeEditor() {
            if (this.saving) return;
            this.editorResizeObserver?.disconnect();
            this.selectedBadgeId = null;
            this.editorOffset = 0;
            this.listContentWidth = 0;
            this.formError = '';
        },
        async afterChange(message) {
            await this.loadData({ silent: true });
            if (this.pageError) this.formError = 'Modification effectuée, mais le rafraîchissement a échoué.';
            else { this.feedback = message; this.formError = ''; }
            await this.$nextTick();
            // Si un changement de statut fait sortir le badge de l'onglet actif,
            // ne pas laisser une fiche orpheline à droite de la liste.
            if (this.selectedBadgeId != null && !this.selectedRow()) {
                this.editorResizeObserver?.disconnect();
                this.selectedBadgeId = null;
                this.editorOffset = 0;
                this.listContentWidth = 0;
            } else {
                this.positionEditor();
            }
        },
        async saveBadge(values) {
            if (this.saving || !this.selectedBadge) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            try {
                await BadgeRepository.patchBadge(this.selectedBadge.id, { ref: values.ref, status: values.status });
                await this.afterChange('Badge mis à jour.');
            } catch (err) { this.formError = 'Impossible d’enregistrer les modifications.'; console.error(err); }
            finally { this.saving = false; }
        },
        async assignBadge(driverId) {
            if (this.saving || !this.selectedBadge) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            try {
                await DriverRepository.addDriverBadge(driverId, this.selectedBadge.id);
                await this.afterChange('Badge associé au chauffeur.');
            } catch (err) {
                this.formError = err.response?.status === 409 ? 'Ce badge est déjà attribué ou ce chauffeur ne peut pas recevoir de badge.' : 'Association impossible.';
                console.error(err);
            } finally { this.saving = false; }
        },
        async unassignBadge() {
            if (this.saving || !this.selectedBadge?.driverId) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            try {
                await DriverRepository.removeDriverBadge(this.selectedBadge.driverId, this.selectedBadge.id);
                await this.afterChange('Badge dissocié du chauffeur.');
            } catch (err) { this.formError = 'Dissociation impossible.'; console.error(err); }
            finally { this.saving = false; }
        },
        async assignVehicle(baliseId) {
            if (this.saving || !this.selectedBadge || !baliseId) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            try {
                await BadgeRepository.addBadgeBalise(this.selectedBadge.id, baliseId);
                await this.afterChange('Véhicule associé au badge.');
            } catch (err) {
                this.formError = 'Impossible d’associer ce véhicule au badge.';
                console.error(err);
            } finally { this.saving = false; }
        },
        async unassignVehicle(baliseId) {
            if (this.saving || !this.selectedBadge || !baliseId) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            try {
                await BadgeRepository.removeBadgeBalise(this.selectedBadge.id, baliseId);
                await this.afterChange('Véhicule dissocié du badge.');
            } catch (err) {
                this.formError = 'Impossible de dissocier ce véhicule du badge.';
                console.error(err);
            } finally { this.saving = false; }
        }
    }
};
</script>

<style scoped>
.badges-page {
    min-height: 65vh;
    background: #f5f7fb;
    color: #17243b;
    padding: 42px 24px 70px;
}

.page-container {
    max-width: 1420px;
    margin: auto;
}

.page-heading {
    margin-bottom: 30px;
}

.eyebrow {
    color: #627c9e;
    font-size: 11px;
    letter-spacing: .16em;
    font-weight: 800;
    margin-bottom: 9px;
}

h1 {
    font-size: clamp(28px, 3vw, 36px);
    font-weight: 750;
    letter-spacing: -.035em;
    margin: 0 0 7px;
}

.page-heading p,
.panel-toolbar p {
    color: #6c7890;
    font-size: 14px;
    margin: 0;
}

/* Hors édition, le tableau utilise toute la largeur disponible. */
.workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
    align-items: start;
    overflow-anchor: none;
}

/* En édition, seule la fenêtre de lecture se réduit, pas le contenu du tableau. */
.workspace.can-manage.has-editor {
    grid-template-columns: minmax(0, 1fr) minmax(340px, 400px);
}

@media (min-width:1101px) {
    .workspace.has-editor .list-panel {
        overflow-x: auto;
        overflow-y: hidden;
        /* hauteur naturelle : aucun scroll vertical interne */
    }

    .workspace.has-editor .panel-toolbar {
        width: var(--list-content-width);
        box-sizing: border-box;
    }

    /* Le scroll horizontal se fait sur le panneau entier, pas sur deux éléments. */
    .workspace.has-editor :deep(.table-scroll) {
        width: var(--list-content-width);
        overflow-x: visible;
    }
}

.list-panel,
.editor-panel {
    min-width: 0;
    background: white;
    border: 1px solid #e3e9f1;
    border-radius: 16px;
    box-shadow: 0 10px 28px rgba(20, 43, 74, .045);
    overflow: hidden;
}

/* L'éditeur reste dans le flux de la page, verticalement centré en regard
   de la ligne sélectionnée. Pas de sticky ni de second défilement. */
.editor-panel {
    margin-top: var(--editor-offset, 0px);
}

.panel-toolbar {
    padding: 24px 26px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    border-bottom: 1px solid #e9edf3;
}

.panel-toolbar h2 {
    font-size: 18px;
    font-weight: 750;
    margin: 0 0 4px;
}

.filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.search-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid #dce4ee;
    border-radius: 9px;
    padding: 0 12px;
    min-width: 240px;
    margin: 0;
}

.search-wrap svg {
    width: 17px;
    height: 17px;
    stroke: #8999ae;
    stroke-width: 2;
    fill: none;
}

.search-wrap input {
    border: 0;
    outline: 0;
    padding: 11px 0;
    width: 100%;
    min-width: 0;
    font-size: 13px;
}

.status-tabs {
    display: flex;
    padding: 3px;
    border: 1px solid #dce4ee;
    border-radius: 9px;
    background: #f5f7fb;
}

.status-tabs button {
    border: 0;
    background: transparent;
    color: #63728a;
    padding: 8px 13px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.status-tabs button.active {
    background: white;
    color: #1b4f95;
    box-shadow: 0 1px 4px rgba(20, 43, 74, .12);
}

.state {
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 30px;
    color: #687892;
}

.state h3 {
    color: #21324b;
    font-size: 18px;
    margin: 0;
}

.state p {
    margin: 0;
}

.loader {
    width: 27px;
    height: 27px;
    border: 3px solid #dae7f4;
    border-top-color: #1b4f95;
    border-radius: 50%;
    animation: spin .8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.message {
    padding: 13px 16px;
    border-radius: 10px;
    margin-bottom: 18px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
}

.message.error {
    color: #a32929;
    background: #fff0f0;
    border: 1px solid #f5cbcb;
}

.message.success {
    color: #136243;
    background: #eaf7ef;
    border: 1px solid #c4e8d2;
}

.message button,
.text-button {
    border: 0;
    background: none;
    font-weight: 700;
    cursor: pointer;
    color: inherit;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@media (max-width:1100px) {
    .workspace.can-manage.has-editor {
        grid-template-columns: 1fr;
    }

    .editor-panel {
        margin-top: 0;
    }
}

@media (max-width:620px) {
    .badges-page {
        padding: 26px 14px 50px;
    }

    .panel-toolbar {
        padding: 20px 16px;
    }

    .filters,
    .search-wrap,
    .status-tabs {
        width: 100%;
    }

    .status-tabs button {
        flex: 1;
    }
}
</style>
